<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import axios from 'axios';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: Number, required: true },
});
const emit = defineEmits(['close', 'created']);

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const defaultRemindAt = () => {
  const t = new Date();
  t.setHours(t.getHours() + 24, 0, 0, 0);
  return t;
};
const remindAt = ref(defaultRemindAt());
const note = ref('');
const cancelOnReply = ref(true);
const submitting = ref(false);

const close = () => emit('close');

const submit = async () => {
  if (!remindAt.value || remindAt.value <= new Date()) {
    useAlert('Pick a time in the future.');
    return;
  }
  submitting.value = true;
  try {
    await axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${props.conversationId}/reminders`,
      {
        remind_at: remindAt.value.toISOString(),
        note: note.value,
        cancel_on_customer_reply: cancelOnReply.value,
      }
    );
    useAlert('Reminder set');
    emit('created');
    close();
  } catch (e) {
    useAlert('Could not set reminder');
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Modal :show="show" :on-close="close">
    <div class="p-6" :style="{ background: 'var(--bloom-bg-primary)' }">
      <h3
        class="text-base font-semibold mb-1"
        :style="{ color: 'var(--bloom-text-primary)' }"
      >
        Set a reminder
      </h3>
      <p
        class="text-xs mb-4"
        :style="{ color: 'var(--bloom-text-tertiary)' }"
      >
        We'll notify you at the time you pick. If the customer replies first,
        the reminder cancels itself automatically.
      </p>

      <label
        class="block text-xs font-medium mb-1"
        :style="{ color: 'var(--bloom-text-secondary)' }"
      >
        When
      </label>
      <DateTimePicker
        :value="remindAt"
        placeholder="Pick a date and time"
        confirm-text="OK"
        class="w-full mb-4"
        @change="d => (remindAt = d)"
      />

      <label
        class="block text-xs font-medium mb-1"
        :style="{ color: 'var(--bloom-text-secondary)' }"
      >
        Note (optional)
      </label>
      <textarea
        v-model="note"
        rows="2"
        placeholder="Why this matters to future-you…"
        class="w-full text-sm rounded-md border px-2 py-1.5 mb-4"
        :style="{ borderColor: 'var(--bloom-border-default)' }"
      />

      <label class="flex items-center gap-2 text-xs mb-4 cursor-pointer">
        <input v-model="cancelOnReply" type="checkbox" />
        <span :style="{ color: 'var(--bloom-text-secondary)' }">
          Cancel if customer replies first
        </span>
      </label>

      <div class="flex justify-end gap-2">
        <Button label="Cancel" variant="ghost" @click="close" />
        <Button
          label="Set reminder"
          :is-loading="submitting"
          :disabled="submitting"
          @click="submit"
        />
      </div>
    </div>
  </Modal>
</template>
